import anime from 'animejs';
import classNames from 'classnames';
import _ from 'lodash';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'react-bootstrap/Image';
import { useTranslation } from 'react-i18next';
import { BsChevronDown } from 'react-icons/bs';
import { createUseStyles } from 'react-jss';
import VisibilityDetector from 'react-visibility-detector';
import { useCloudinary } from '../../hooks/use-cloudinary';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  parseContainerBackgroundShade,
  parseLayoutBoxShadow,
} from '../../utils/manager/layout-manager';
import {
  parseMinMaxValue,
  parsePriceValue,
} from '../../utils/manager/price-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

const animeteFromLeft =
  'animate__animated animate__fadeInLeft animate__faster animate__delay-1s';
const animeteFromRight =
  'animate__animated animate__fadeInRight animate__faster animate__delay-1s';

const imageMargin = layoutDefaults.smallHorizontalMargin;

function MosaicCardPrivate({
  category,
  siblings,
  length,
  row,
  col,
  totalRows,
  totalCols,
  onSelect,
  className,
}) {
  const { menuLayout, menuInfo } = useStorage();
  const { analyticsFeatureUseEvent } = useAnalytics();
  const isFirstRow = useMemo(() => row === 0, [row]);
  const isLastRow = useMemo(() => row + 1 === totalRows, [row, totalRows]);
  const isFirstCol = useMemo(() => col === 0, [col]);
  const isLastCol = useMemo(
    () => col + 1 === totalCols || col + 1 + row * totalCols >= length,
    [col, length, row, totalCols]
  );
  const [selected, setSelected] = useState();
  const containerRef = useRef();
  const { transform } = useCloudinary();
  const classes = useStyles({
    isEmpty: category == null,
    layout: menuLayout,
    isFirstRow,
    isLastRow,
    isFirstCol,
    isLastCol,
    selected,
    // image: _.head(category?.images),
  });

  const sinblingsHaveImage = useMemo(
    () => siblings?.filter((s) => _.head(s?.images)?.url != null)?.length > 0,
    [siblings]
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (category == null) return;

    const shade = selected ? -20 : -3;

    anime({
      targets: containerRef.current,
      backgroundColor: [
        containerRef.current.style.backgroundColor,
        parseContainerBackgroundShade(menuLayout, shade),
      ],
      easing: 'linear',
      duration: selected ? 10 : 150,
      delay: selected ? 0 : 50,
    });
  }, [menuLayout, selected, category]);

  const limits = useMemo(() => {
    let min = null;
    let max = null;

    category?.items?.forEach((item) => {
      // item.prices?.forEach((price) => {
      const parsedMin = parseMinMaxValue(category, item, {
        bypassItemPrices: false,
        computeMin: true,
      });

      const parsedMax = parseMinMaxValue(category, item, {
        bypassItemPrices: false,
        computeMin: false,
      });

      if (min?.value == null || min.value > parsedMin) {
        min = {
          value: parsedMin,
        };
      }

      if (max?.value == null || max.value < parsedMax) {
        max = {
          value: parsedMax,
        };
      }
      // });
    });

    return {
      min,
      max,
    };
  }, [category]);

  const onClick = () => {
    setSelected(true);
    analyticsFeatureUseEvent('category card click');

    onSelect?.call(this, category);
  };

  const onVisibilityChange = ({ visible }) => {
    if (visible) {
      setTimeout(() => {
        setSelected(false);
      }, 500);
    }
  };

  return (
    <div
      ref={containerRef}
      className={classNames(
        classes.container,
        isFirstCol ? animeteFromLeft : animeteFromRight,
        className
      )}
      style={{
        '--animate-delay': `${(2 + totalRows - row - 1 + col * 3) / 10}s`,
      }}
      onClick={onClick}
      aria-hidden
    >
      <VisibilityDetector
        className={classes.content}
        onVisibilityChange={onVisibilityChange}
      >
        <div className={classes.name}>{category?.name}</div>

        <div className={classes.imageContainer}>
          {!_.isEmpty(_.head(category?.images)?.url) ? (
            <Image
              className={classes.image}
              src={transform(_.head(category?.images)?.url, 2)}
              fluid
            />
          ) : (
            category?.name &&
            sinblingsHaveImage && (
              <div className={classes.noImage}>{t('mosaic.no-photo')}</div>
            )
          )}

          {/* {category?.image && (
            <Image
              className={classes.image}
              src={transform(category?.image?.url, 2)}
              fluid
            />
          )} */}

          {category && (
            <div className={classes.price}>
              {limits.min?.value !== limits.max?.value ? (
                <>
                  {t('mosaic.from-to', {
                    from: parsePriceValue(limits.min),
                    to: parsePriceValue(limits.max),
                    symbol: menuInfo.i18n?.currency?.symbol,
                  })}
                </>
              ) : (
                <>
                  {menuInfo.i18n?.currency?.symbol}{' '}
                  {parsePriceValue(limits.max)}
                </>
              )}{' '}
              <BsChevronDown />
            </div>
          )}
        </div>
      </VisibilityDetector>
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flex: 1,
    // flexDirection: "column",
    // justifyContent: "flex-end",
    // alignItems: "stretch",

    // fontWeight: "bold",
    cursor: 'pointer',
    paddingTop: layoutDefaults.smallVerticalMargin,
    paddingBottom: layoutDefaults.smallVerticalMargin,
    paddingLeft: imageMargin,
    paddingRight: imageMargin,
    // margin: layoutDefaults.smallerVerticalMargin,

    // backgroundImage: ({ image }) => `url("${image?.url}")`,
    // backgroundColor: ({ layout, isEmpty }) =>
    //   !isEmpty && parseContainerBackgroundShade(layout, -3),
    backgroundPosition: 'center',
    backgroundSize: 'cover',

    marginLeft: ({ isFirstCol }) =>
      isFirstCol ? 0 : layoutDefaults.smallerHorizontalMargin,
    marginRight: ({ isLastCol }) =>
      isLastCol ? 0 : layoutDefaults.smallerHorizontalMargin,
    marginTop: ({ isFirstRow }) =>
      isFirstRow ? 0 : layoutDefaults.smallVerticalMargin,

    // border: ({ layout }) => `1px solid ${parseDefaultTextColor(layout, 0.3)}`,
    border: ({ layout, isEmpty }) =>
      !isEmpty && `1px solid ${parseContainerBackgroundShade(layout, 12)}`,
    boxShadow: ({ layout, isEmpty }) => parseLayoutBoxShadow(layout, isEmpty),
    borderRadius: layoutDefaults.radius,

    // border: `1px solid white`,
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  name: {
    fontSize: layoutDefaults.smallFontSize,
    fontWeight: 'bold',
    textTransform: ({ layout }) => layout.text.title_font_style,
    marginBottom: layoutDefaults.smallerVerticalMargin,
    paddingLeft: layoutDefaults.smallHorizontalMargin - imageMargin,
    paddingRight: layoutDefaults.smallHorizontalMargin - imageMargin,

    // backgroundColor: ({ layout }) => parseContainerBackgroundColor(layout, 0.7),

    // border: `1px solid white`,
  },
  imageContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  image: {
    // alignSelf: "flex-end",
    // justifySelf: "flex-end",
    // border: `1px solid white`,
    // borderRadius: layoutDefaults.radius,
  },
  noImage: {
    display: 'flex',
    flexGrow: 1,
    fontSize: layoutDefaults.smallestFontSize,
    textTransform: 'uppercase',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: layoutDefaults.opacity,
    // border: `1px solid white`,
  },
  price: {
    fontSize: layoutDefaults.smallerFontSize,
    opacity: layoutDefaults.opacity,
    textAlign: 'right',
    marginTop: layoutDefaults.smallVerticalMargin,
    paddingLeft: layoutDefaults.smallHorizontalMargin - imageMargin,
    paddingRight: layoutDefaults.smallHorizontalMargin - imageMargin,

    // border: `1px solid red`,
    // backgroundColor: ({ layout }) => parseContainerBackgroundColor(layout, 0.7),

    // border: `1px solid white`,
  },
});

const MosaicCard = memo(MosaicCardPrivate, _.isEqual);
export { MosaicCard };
